import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EvenementFrise } from './useEspaceApprenant';

// ---------------------------------------------------------------------------
// Frise du dossier vue par l'équipe (CDC §8.3), et ses deux registres :
// aménagements d'examen et refus exprimés par l'apprenant. Qui peut les tenir
// vient de l'API (`peutSaisir`), l'écran ne rejoue pas la règle.
// ---------------------------------------------------------------------------

export type StatutAmenagement = 'DEMANDE' | 'ACCORDE' | 'REFUSE';
export type ObjetRefus = 'ENTREPRISE' | 'ACCOMPAGNEMENT' | 'CONVOCATION' | 'AUTRE';

export const LIBELLES_STATUT_AMENAGEMENT: Record<StatutAmenagement, string> = {
  DEMANDE: 'Demandé',
  ACCORDE: 'Accordé',
  REFUSE: 'Refusé',
};

export const LIBELLES_OBJET_REFUS: Record<ObjetRefus, string> = {
  ENTREPRISE: 'Entreprise proposée',
  ACCOMPAGNEMENT: 'Action d’accompagnement',
  CONVOCATION: 'Convocation',
  AUTRE: 'Autre',
};

export interface Amenagement {
  id: string;
  demandeLe: string;
  amenagement: string;
  statut: StatutAmenagement;
  decideLe: string | null;
  commentaire: string | null;
  saisiPar: string | null;
}

export interface RefusExprime {
  id: string;
  exprimeLe: string;
  objet: ObjetRefus;
  precision: string | null;
  motif: string;
  suivi: string | null;
  suiviLe: string | null;
  saisiPar: string | null;
}

export interface FriseEquipe {
  evenements: EvenementFrise[];
  amenagements: Amenagement[];
  refus: RefusExprime[];
  peutSaisir: boolean;
}

function cle(apprenantId: string): readonly unknown[] {
  return ['frise-dossier', apprenantId];
}

export function useFriseEquipe(apprenantId: string) {
  return useQuery({
    queryKey: cle(apprenantId),
    queryFn: () => appelApi<FriseEquipe>(`/apprenants/${apprenantId}/frise`),
  });
}

/** Chaque geste rend la frise à jour : l'écran la remplace sans relecture. */
function useGesteFrise<T>(apprenantId: string, appel: (variables: T) => Promise<FriseEquipe>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: appel,
    onSuccess: (frise) => client.setQueryData(cle(apprenantId), frise),
  });
}

export function useCreerAmenagement(apprenantId: string) {
  return useGesteFrise(apprenantId, (corps: { demandeLe: string; amenagement: string; commentaire?: string }) =>
    appelApi<FriseEquipe>(`/apprenants/${apprenantId}/amenagements-examen`, { methode: 'POST', corps }),
  );
}

export function useDeciderAmenagement(apprenantId: string) {
  return useGesteFrise(apprenantId, ({ id, ...corps }: { id: string; statut: StatutAmenagement; commentaire?: string }) =>
    appelApi<FriseEquipe>(`/apprenants/${apprenantId}/amenagements-examen/${id}`, { methode: 'PATCH', corps }),
  );
}

export function useCreerRefus(apprenantId: string) {
  return useGesteFrise(
    apprenantId,
    (corps: { exprimeLe: string; objet: ObjetRefus; precision?: string; motif: string }) =>
      appelApi<FriseEquipe>(`/apprenants/${apprenantId}/refus`, { methode: 'POST', corps }),
  );
}

export function useSuivreRefus(apprenantId: string) {
  return useGesteFrise(apprenantId, ({ id, suivi }: { id: string; suivi: string }) =>
    appelApi<FriseEquipe>(`/apprenants/${apprenantId}/refus/${id}/suivi`, { methode: 'PATCH', corps: { suivi } }),
  );
}
